<template>
  <div>
    <!-- Custom Tab Navigation & Filters -->
    <div class="flex items-center justify-between gap-[16px] mb-6 flex-wrap">
      <Tabs
        v-model="activeTab"
        @input="switchTab"
      >
        <TabItem value="komship">
          Komship
        </TabItem>
        <TabItem value="nonkomship">
          Non Komship
        </TabItem>
        <TabItem value="marketplace">
          Marketplace
        </TabItem>
      </Tabs>

      <div class="flex items-center gap-[8px] flex-wrap">
        <UIInput
          v-if="activeTab !== 'komship'"
          v-model="search"
          :placeholder="`Cari ${activeTab === 'nonkomship' ? 'Outbond ID' : 'Order No.'}`"
          class="w-auto max-w-[250px]"
          @input="handleSearchChange"
        >
          <template #suffix>
            <span class="k-search-normal-1 text-[16px] text-[#0A0A0A]" />
          </template>
        </UIInput>

        <Select
          v-model="selectedMitra"
          :options="mitraOptions"
          :loading="loadingMitra"
          :has-next-page="!mitraPagination.isAllLoaded"
          option-label="name"
          :reduce="item => item.id"
          placeholder="Nama Mitra"
          class="w-[140px]"
          clearable
          @input="handleChangeMitra"
          @on-search="handleMitraSearch"
          @on-intersect="handleMitraIntersect"
        />

        <Select
          v-model="selectedPartner"
          :options="partnerOptions"
          :loading="loadingPartner"
          :has-next-page="!partnerPagination.isAllLoaded"
          option-label="brand_name"
          :reduce="item => item.partner_id"
          placeholder="Nama Partner"
          class="w-[140px]"
          clearable
          @input="handleChangePartner"
          @on-search="handlePartnerSearch"
          @on-intersect="handlePartnerIntersect"
        />

        <Select
          v-model="selectedTransactionType"
          :options="transactionTypeOptions"
          option-label="text"
          :reduce="item => item.value"
          placeholder="Status"
          class="w-[120px]"
          @input="handleChangeTransactionType"
        />
      </div>
    </div>

    <!-- Tab Content -->
    <div class="pb-[16px]">
      <div class="mb-[8px] text-[#333333] flex items-center justify-start gap-[8px] [&_p]:mb-0">
        <p>Total Order <span class="capitalize">{{ parsedTabKey === 'kompack' ? 'Non-Komship' : parsedTabKey }}</span></p>
        <p>:
          <b-spinner
            v-if="loadingSummaries"
            small
            variant="primary"
            class="mb-[2px]"
          />
          <span v-else>{{ summariesData[`total_order_${parsedTabKey}`] }}</span>
        </p>
      </div>
      <FulfillmentKomshipCard
        v-if="activeTab === 'komship'"
        ref="fulfillmentKomshipCard"
        :period="activePeriod"
        :status="selectedTransactionType"
        :warehouse-id="selectedMitra"
        :partner-id="selectedPartner"
      />
      <FulfillmentNonKomship
        v-if="activeTab === 'nonkomship'"
        ref="fulfillmentNonKomshipCard"
        :period="activePeriod"
        :status="selectedTransactionType"
        :search="search"
        :warehouse-id="selectedMitra"
        :partner-id="selectedPartner"
      />
      <FulfillmentMarketplace
        v-if="activeTab === 'marketplace'"
        ref="fulfillmentMarketplaceCard"
        :period="activePeriod"
        :status="selectedTransactionType"
        :search="search"
        :warehouse-id="selectedMitra"
        :partner-id="selectedPartner"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { YEAR_MONTH_DAY } from '@/libs/filterDate'
import {
  Tabs,
  TabItem,
  Input as UIInput,
  Select,
} from '@core/components/ui'
import { transactionTypeOptions } from '../config'

export default {
  name: 'TransaksiBiayaFulfillmentCard',
  components: {
    Tabs,
    TabItem,
    UIInput,
    Select,
    FulfillmentKomshipCard: () => import('./FulfillmentKomshipCard.vue'),
    FulfillmentNonKomship: () => import('./FulfillmentNonKomship.vue'),
    FulfillmentMarketplace: () => import('./FulfillmentMarketplace.vue'),
  },
  props: {
    /** @type {Object} Date range filter { startDate, endDate } */
    period: {
      type: Object,
      required: true,
    },
  },
  data() {
    const query = this.$route.query || {}
    return {
      activeTab: query.tab || 'nonkomship',
      activePeriod: this.period,
      search: query.search || '',
      selectedMitra: query.warehouse_id ? (Number(query.warehouse_id) || query.warehouse_id) : null,
      selectedPartner: query.partner_id ? (Number(query.partner_id) || query.partner_id) : null,
      partnerOptions: [],
      loadingPartner: false,
      partnerPagination: {
        offset: 0,
        limit: 50,
        isAllLoaded: false,
        isFetching: false,
      },
      mitraOptions: [],
      loadingMitra: false,
      mitraPagination: {
        offset: 0,
        limit: 50,
        isAllLoaded: false,
        isFetching: false,
      },
      selectedTransactionType: query.status || null,
      loadingSummaries: true,
      summariesData: null,
    }
  },
  computed: {
    transactionTypeOptions() {
      return transactionTypeOptions[this.activeTab] || []
    },
    parsedTabKey() {
      const tab = this.activeTab || 'komship'
      return tab === 'nonkomship' ? 'kompack' : tab
    },
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        this.syncStateFromQuery(newQuery)
      },
    },
    period: {
      handler() {
        this.activePeriod = this.period
        this.$nextTick(() => {
          this.handleRefreshData()
        })
      },
    },
  },
  created() {
    this.debouncedUpdateQuery = _.debounce(this.updateQuery, 500)
    if (this.selectedMitra) {
      this.resolveMitraFromUrl(this.selectedMitra)
    }
    if (this.selectedPartner) {
      this.resolvePartnerFromUrl(this.selectedPartner)
    }
  },
  mounted() {
    this.fetchSummaries()
    this.fetchFilterOptions()
  },
  beforeDestroy() {
    if (this.debouncedUpdateQuery && this.debouncedUpdateQuery.cancel) {
      this.debouncedUpdateQuery.cancel()
    }
  },
  methods: {
    /**
     * Synchronize component state with route query parameters
     * @param {Object} [query=this.$route.query]
     */
    syncStateFromQuery(query = this.$route.query) {
      const newTab = query.tab || 'komship'
      if (this.activeTab !== newTab) {
        this.activeTab = newTab
        this.fetchFilterOptions()
      }

      this.selectedTransactionType = query.status || null
      this.search = query.search || ''
      const newMitraId = query.warehouse_id ? (Number(query.warehouse_id) || query.warehouse_id) : null
      const newPartnerId = query.partner_id ? (Number(query.partner_id) || query.partner_id) : null
      this.selectedPartner = newPartnerId
      this.selectedMitra = newMitraId
      if (newMitraId) {
        this.resolveMitraFromUrl(newMitraId)
      }
      if (newPartnerId) {
        this.resolvePartnerFromUrl(newPartnerId)
      }
    },
    /**
     * Update route query parameters if changed
     * @param {Object} newParams
     */
    updateQuery(newParams) {
      const currentQuery = { ...this.$route.query }
      const newQuery = { ...currentQuery }

      Object.keys(newParams).forEach(key => {
        const val = newParams[key]
        if (val === null || val === undefined || val === '') {
          delete newQuery[key]
        } else {
          newQuery[key] = String(val)
        }
      })

      const currentKeys = Object.keys(currentQuery)
      const newKeys = Object.keys(newQuery)
      const isChanged = currentKeys.length !== newKeys.length
        || newKeys.some(key => String(currentQuery[key]) !== String(newQuery[key]))

      if (isChanged) {
        this.$router.replace({
          path: this.$route.path,
          query: newQuery,
        }).catch(err => {
          if (err && err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },
    /**
     * Fetch initial filter options for partner and mitra
     */
    fetchFilterOptions() {
      if (this.selectedPartner) {
        this.resolvePartnerFromUrl(this.selectedPartner)
      } else if (!this.partnerOptions.length && !this.loadingPartner) {
        this.loadingPartner = true
        this.fetchPartnerPage().finally(() => {
          this.loadingPartner = false
        })
      }

      if (this.selectedMitra) {
        this.resolveMitraFromUrl(this.selectedMitra)
      } else if (!this.mitraOptions.length && !this.loadingMitra) {
        this.loadingMitra = true
        this.fetchMitraPage().finally(() => {
          this.loadingMitra = false
        })
      }
    },
    /**
     * Fetch a paginated chunk of mitra warehouses and append unique entries
     * @returns {Promise<Array>}
     */
    async fetchMitraPage() {
      if (this.mitraPagination.isAllLoaded || this.mitraPagination.isFetching) {
        return []
      }
      this.mitraPagination.isFetching = true
      try {
        const { data } = await this.$http_new.get('/komship/api/v1/warehouses/kompack', {
          params: {
            page: this.mitraPagination.offset,
            limit: this.mitraPagination.limit,
          },
        })
        const items = data?.data || []
        if (!items.length) {
          this.mitraPagination.isAllLoaded = true
          return []
        }

        const existingIds = new Set(this.mitraOptions.map(p => String(p.id)))
        const newItems = items.filter(p => !existingIds.has(String(p.id)))
        this.mitraOptions.push(...newItems)

        this.mitraPagination.offset += items.length
        if (items.length < this.mitraPagination.limit || newItems.length === 0) {
          this.mitraPagination.isAllLoaded = true
        }

        return items
      } catch (error) {
        return []
      } finally {
        this.mitraPagination.isFetching = false
      }
    },
    /**
     * Sequentially fetch mitra pages until the specified warehouse ID is found
     * @param {number|string} warehouseId
     */
    async resolveMitraFromUrl(warehouseId) {
      if (!warehouseId) return
      const alreadyExists = this.mitraOptions.some(p => String(p.id) === String(warehouseId))
      if (alreadyExists) return

      this.loadingMitra = true
      try {
        while (!this.mitraPagination.isAllLoaded) {
          // eslint-disable-next-line no-await-in-loop
          const items = await this.fetchMitraPage()
          if (!items.length) break

          const match = this.mitraOptions.some(p => String(p.id) === String(warehouseId))
          if (match) {
            break
          }
        }

        const exists = this.mitraOptions.some(p => String(p.id) === String(warehouseId))
        if (!exists && this.mitraPagination.isAllLoaded) {
          this.selectedMitra = null
          this.updateQuery({ warehouse_id: null })
        }
      } finally {
        this.loadingMitra = false
      }
    },
    handleMitraIntersect() {
      if (this.mitraPagination.isAllLoaded || this.mitraPagination.isFetching) return
      this.fetchMitraPage()
    },
    async handleMitraSearch(query) {
      if (query && query.trim() && !this.mitraPagination.isAllLoaded && !this.mitraPagination.isFetching) {
        while (!this.mitraPagination.isAllLoaded) {
          // eslint-disable-next-line no-await-in-loop
          const items = await this.fetchMitraPage()
          if (!items.length) break
        }
      }
    },
    /**
     * Fetch a paginated chunk of partners and append unique entries
     * @returns {Promise<Array>}
     */
    async fetchPartnerPage() {
      if (this.partnerPagination.isAllLoaded || this.partnerPagination.isFetching) {
        return []
      }
      this.partnerPagination.isFetching = true
      try {
        const { data } = await this.$http_new.get('/auth/api/v1/admin/dashboard/partners', {
          params: {
            offset: this.partnerPagination.offset,
            limit: this.partnerPagination.limit,
          },
        })
        const items = data?.data || []
        if (!items.length) {
          this.partnerPagination.isAllLoaded = true
          return []
        }

        const formatted = items.map(item => ({
          ...item,
          id: item.partner_id ?? item.user_id,
          partner_id: item.partner_id ?? item.user_id,
          brand_name: item.brand_name || item.email || '-',
        }))

        const existingIds = new Set(this.partnerOptions.map(p => String(p.partner_id)))
        const newItems = formatted.filter(p => !existingIds.has(String(p.partner_id)))
        this.partnerOptions.push(...newItems)

        this.partnerPagination.offset += items.length
        if (items.length < this.partnerPagination.limit) {
          this.partnerPagination.isAllLoaded = true
        }

        return formatted
      } catch (error) {
        return []
      } finally {
        this.partnerPagination.isFetching = false
      }
    },
    /**
     * Sequentially fetch partner pages until the specified partner ID is found
     * @param {number|string} partnerId
     */
    async resolvePartnerFromUrl(partnerId) {
      if (!partnerId) return
      const alreadyExists = this.partnerOptions.some(p => String(p.partner_id) === String(partnerId))
      if (alreadyExists) return

      this.loadingPartner = true
      try {
        while (!this.partnerPagination.isAllLoaded) {
          // eslint-disable-next-line no-await-in-loop
          const items = await this.fetchPartnerPage()
          if (!items.length) break

          const match = this.partnerOptions.some(p => String(p.partner_id) === String(partnerId))
          if (match) {
            break
          }
        }

        const exists = this.partnerOptions.some(p => String(p.partner_id) === String(partnerId))
        if (!exists && this.partnerPagination.isAllLoaded) {
          this.selectedPartner = null
          this.updateQuery({ partner_id: null })
        }
      } finally {
        this.loadingPartner = false
      }
    },
    handlePartnerIntersect() {
      if (this.partnerPagination.isAllLoaded || this.partnerPagination.isFetching) return
      this.fetchPartnerPage()
    },
    async handlePartnerSearch(query) {
      if (query && query.trim() && !this.partnerPagination.isAllLoaded && !this.partnerPagination.isFetching) {
        while (!this.partnerPagination.isAllLoaded) {
          // eslint-disable-next-line no-await-in-loop
          const items = await this.fetchPartnerPage()
          if (!items.length) break
        }
      }
    },
    /**
     * Refresh summaries and active child tab data
     */
    async handleRefreshData() {
      this.fetchSummaries()

      if (this.activeTab === 'komship' && this.$refs.fulfillmentKomshipCard) {
        await this.$refs.fulfillmentKomshipCard.handleRefreshData()
        return
      }

      if (this.activeTab === 'nonkomship' && this.$refs.fulfillmentNonKomshipCard) {
        await this.$refs.fulfillmentNonKomshipCard.handleRefreshData()
        return
      }

      if (this.activeTab === 'marketplace' && this.$refs.fulfillmentMarketplaceCard) {
        await this.$refs.fulfillmentMarketplaceCard.handleRefreshData()
      }
    },
    switchTab(tabName) {
      this.activeTab = tabName
      this.selectedTransactionType = null
      this.selectedMitra = null
      this.selectedPartner = null
      this.search = ''
      this.updateQuery({
        tab: tabName,
        status: null,
        search: null,
        warehouse_id: null,
        partner_id: null,
      })
      this.fetchFilterOptions()
    },
    handleSearchChange(value) {
      this.debouncedUpdateQuery({ search: value ? value.trim() : null })
    },
    handleChangeMitra(value) {
      this.updateQuery({ warehouse_id: value || null })
    },
    handleChangePartner(value) {
      this.updateQuery({ partner_id: value || null })
    },
    handleChangeTransactionType(value) {
      this.updateQuery({ status: value || null })
    },
    /**
     * Fetch total order summaries for the selected period
     */
    async fetchSummaries() {
      this.loadingSummaries = true
      try {
        const { data } = await this.$http_new.get('/komship/api/v1/admin/kompack/total-orders', {
          params: {
            start_date: YEAR_MONTH_DAY(this.period.startDate),
            end_date: YEAR_MONTH_DAY(this.period.endDate),
          },
        })
        this.summariesData = data.data
      } catch (error) {
        this.$toast_error({
          message: 'Gagal mengambil data ringkasan, silahkan coba lagi',
        })
      }
      this.loadingSummaries = false
    },
  },
}
</script>
